import type { SupabaseClient } from '@supabase/supabase-js';

/** Refresh when the access token is already expired or within this window. */
const REFRESH_IF_EXPIRES_WITHIN_MS = 2 * 60 * 1000;

/**
 * Keep Supabase sessions alive across long idle tabs (DEF-418 residual).
 *
 * Access JWTs expire at ~60m for every role. Background tabs throttle
 * `autoRefreshToken` timers, so SSI (who must never idle-timeout) can still
 * bounce on the next click. On focus / visibility, refresh when near expiry.
 *
 * Safe for all roles — non-SSI idle logout remains owned by SessionTimeoutModal.
 */
export function attachSessionWakeRefresh(supabase: SupabaseClient): () => void {
  const maybeRefresh = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    void supabase.auth.getSession().then(({ data }) => {
      const session = data.session;
      if (!session?.expires_at) return;
      const expiresAtMs = session.expires_at * 1000;
      if (expiresAtMs - Date.now() > REFRESH_IF_EXPIRES_WITHIN_MS) return;
      void supabase.auth.refreshSession();
    });
  };

  document.addEventListener('visibilitychange', maybeRefresh);
  window.addEventListener('focus', maybeRefresh);
  return () => {
    document.removeEventListener('visibilitychange', maybeRefresh);
    window.removeEventListener('focus', maybeRefresh);
  };
}
